// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, BrandModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SignInButton from "@keller/App/Components/Common/SignInButton";
import ProductPreviousPurchase from "@keller/App/Components/Product/ProductPreviousPurchase";
import { getIsNonPurchasable, xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import ProductAddToCartButton from "@keller/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@keller/Overrides/Components/ProductAddToListLink";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@keller/Overrides/Components/ProductPartNumbers";
import { color, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    query?: string;
    doSearch?: () => void;
    products: ProductContextModel[];
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        isAuthenticated: xIsAuthenticated(state),
        isNonPurchasable: getIsNonPurchasable(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface AutoCompleteProductStyles {
    productWrapper?: InjectableCss;
    productContainer?: InjectableCss;
    productInfoContainer?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    actionsContainer?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productQuantityOrderedStyles?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    productBrandStyles?: ProductBrandStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // XNG-Changes
    titleWrapper?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
    productStyles?: AutoCompleteProductStyles;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        size: 15,
        weight: "normal",
        ellipsis: true,
        css: css`
            text-align: left;
            letter-spacing: 0;
            line-height: 25px;
            margin-right: 10px;
            & > ${TypographyStyle} {
                display: block;
                font-size: 20px;
                font-weight: bold;
                letter-spacing: 0;
                line-height: 25px;
            }
        `,
    },
    container: {
        css: css`
            flex-grow: 1;
            display: flex;
        `,
    },

    image: {
        width: "72px",
        height: "72px",
        css: css`
            margin-right: 16px;
            flex-shrink: 0;

            img {
                height: auto;
                max-height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            flex-wrap: wrap;
            padding-bottom: 8px;
            margin-bottom: 10px;
            border-bottom: 1px solid ${color("common.ui6")};
        `,
    },
    viewAllLink: {
        typographyProps: {
            size: 14,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 16px;
            `,
        },
    },
    productStyles: {
        productWrapper: {
            css: css`
                display: flex;
                padding-bottom: 12px;
                flex-wrap: nowrap;
                ${xScreen("lte", "sm")} {
                    flex-direction: column;
                }

                &:not(.last-item) {
                    border-bottom: 1px solid ${color("common.border")};
                    margin-bottom: 12px;
                }
            `,
        },
        productDescriptionStyles: {
            productDetailLink: {
                css: css`
                    width: auto;
                    margin-bottom: 8px;
                `,
                typographyProps: {
                    size: 12,
                    weight: "bold",
                    css: css`
                        letter-spacing: 0;
                        line-height: 13px;
                        ${wrapInContainerStyles}
                    `,
                },
            },
        },
        productContainer: {
            css: css`
                flex-grow: 1;
                display: flex;
                ${xScreen("lte", "xs")} {
                    flex-wrap: wrap;
                }
            `,
        },
        productInfoContainer: {
            css: css`
                flex-grow: 1;
                display: flex;
                flex-direction: column;
                margin-right: 20px;
                ${xScreen("lte", "xs")} {
                    margin-right: 0;
                    margin-bottom: 20px;
                }
            `,
        },
        actionsContainer: {
            css: css`
                flex-grow: 0;
                flex-shrink: 0;
                flex-basis: 160px;
                display: flex;
                align-items: flex-start;
                justify-content: flex-end;

                &.sign-in {
                    flex-basis: 185px;
                }

                &.add-to-cart > *:first-child {
                    margin-right: 12px;
                }
                ${xScreen("lte", "xs")} {
                    &,
                    &.sign-in {
                        flex-basis: auto;
                        width: 100%;
                    }
                }
            `,
        },
        productImage: {
            linkWrappingImage: {
                css: css`
                    height: 72px;
                    width: 72px;
                    margin-right: 16px;
                    flex-shrink: 0;
                    ${xScreen("lte", "sm")} {
                        margin: 0 auto 20px;
                    }
                `,
            },
            img: {
                width: "72px",
                height: "72px",
                css: css`
                    height: auto;
                    width: auto;
                    max-height: 100%;
                    overflow: hidden;
                `,
            },
        },
        productQuantityOrderedStyles: {
            sizeVariant: "small",
            cssOverrides: {
                formField: css`
                    width: 52px;
                    height: 30px;
                `,
                inputSelect: css`
                    padding: 4px;
                    text-align: center;
                    width: 52px;
                    height: 30px;
                `,
            },
        },
        productBrandStyles: {
            nameLink: {
                css: css`
                    width: auto;
                    margin-bottom: 8px;
                `,
                typographyProps: {
                    size: 12,
                    weight: "normal",
                    css: css`
                        letter-spacing: 0;
                        line-height: 13px;
                        ${wrapInContainerStyles}
                    `,
                },
            },
        },
        productPartNumbersStyles: {
            container: {
                css: css`
                    margin-bottom: 8px;
                `,
            },
        },
        signInButton: {
            css: css`
                ${xScreen("lte", "xs")} {
                    width: 100%;
                }
            `,
        },
        addToListLink: {
            button: {
                css: css`
                    ${xScreen("lte", "xs")} {
                        width: 100%;
                    }
                `,
            },
        },
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Recommended Products for")}
                        <Typography ellipsis={true} data-test-selector="autocompleteProducts_Header">
                            {this.props.query}
                        </Typography>
                    </Typography>
                    <Link {...styles.viewAllLink} onClick={this.props.doSearch} data-test-selector="search-view-all">
                        {translate("View All")}
                    </Link>
                </StyledWrapper>
                {products.map((productContext, index) => (
                    <ProductContext.Provider key={productContext.product.id} value={productContext}>
                        <StyledWrapper
                            {...styles.productStyles?.productWrapper}
                            className={`${index === products.length - 1 ? "last-item" : ""}`}
                        >
                            <ProductImage
                                product={productContext}
                                extendedStyles={styles.productStyles?.productImage}
                            />
                            <StyledWrapper {...styles.productStyles?.productContainer}>
                                <StyledWrapper {...styles.productStyles?.productInfoContainer}>
                                    <StyledWrapper>
                                        <ProductBrand
                                            brand={productContext.product?.brand as BrandModel}
                                            showLogo={false}
                                            extendedStyles={styles.productStyles?.productBrandStyles}
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper>
                                        <ProductDescription
                                            product={productContext}
                                            extendedStyles={styles.productStyles?.productDescriptionStyles}
                                        />
                                    </StyledWrapper>
                                    <ProductPartNumbers
                                        productNumber={productContext.product.productNumber}
                                        showProductNumber={!productContext.product.isVariantParent}
                                        manufacturerItem={productContext.product.manufacturerItem}
                                        extendedStyles={styles.productStyles?.productPartNumbersStyles}
                                    />
                                    <ProductPreviousPurchase product={productContext.product} />
                                </StyledWrapper>
                                <StyledWrapper
                                    {...styles.productStyles?.actionsContainer}
                                    className={`${
                                        this.props.isAuthenticated && productContext.product.canAddToCart
                                            ? "add-to-cart"
                                            : "sign-in"
                                    }`}
                                >
                                    {!this.props.isAuthenticated ? (
                                        <SignInButton extendedStyles={styles.productStyles?.signInButton} />
                                    ) : this.props.isNonPurchasable || !productContext.product.canAddToCart ? (
                                        <ProductAddToListLink
                                            showAsButton={true}
                                            data-test-selector={`actionsAddToList${productContext.product.id}`}
                                            extendedStyles={styles.productStyles?.addToListLink}
                                        />
                                    ) : (
                                        <>
                                            <ProductQuantityOrdered
                                                labelOverride=""
                                                extendedStyles={styles.productStyles?.productQuantityOrderedStyles}
                                                id={`${productContext.product.id}-qty`}
                                            />
                                            <ProductAddToCartButton
                                                data-test-selector={`${productContext.product.id}-addToCartBtn`}
                                                iconOnly={true}
                                                extendedStyles={styles.productStyles?.productAddToCartButtonStyles}
                                            />
                                        </>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </ProductContext.Provider>
                ))}
            </>
        );
    }
}

export default connect(mapStateToProps)(AutocompleteProducts);
